import api, { unwrap } from './client'
import type {
  BuildEvidence,
  LinearTask,
  LinearTaskBuildBucketFilters,
  LinearTaskBuildBuckets,
  LinearTaskEvent,
  LinearTaskProgressEvent,
  LinearTaskPullRequestFilters,
  MergePullRequestsResponse,
  Paginated,
} from '@/types/models'

// The status/date-range/hide-no-op-skipped filters listPullRequests and
// listPullRequestBuckets both take — shared here so the two never drift on
// how one of them is serialized into query params.
function sharedFilterParams(
  filters: Pick<LinearTaskPullRequestFilters, 'status' | 'created_after' | 'created_before' | 'hide_no_op_skipped'>,
): Record<string, string> {
  const params: Record<string, string> = {}
  if (filters.status) params['filter[status]'] = filters.status
  if (filters.created_after) params['filter[created_after]'] = filters.created_after
  if (filters.created_before) params['filter[created_before]'] = filters.created_before
  if (filters.hide_no_op_skipped) params['filter[hide_no_op_skipped]'] = '1'
  return params
}

export const linearTasksApi = {
  // Tasks with their opened pull requests eager-loaded — paginated over the
  // tickets, so a multi-repo run reads as one ticket with several PRs. A
  // flat, page-at-a-time list: callers that just want "the N most recent
  // tasks matching a filter" read this one.
  async listPullRequests(filters: LinearTaskPullRequestFilters = {}): Promise<Paginated<LinearTask>> {
    const params: Record<string, string | number> = { ...sharedFilterParams(filters) }
    if (filters.page) params.page = filters.page
    if (filters.per_page) params.per_page = filters.per_page

    const { data } = await api.get<Paginated<LinearTask>>('/linear-tasks/pull-requests', { params })
    return data
  },

  // The Builds page's own view: every matching task already sorted into its
  // four sections — Ready to merge, Fixing, In progress, Failed — and each
  // independently paginated. See LinearTaskBuildBucketFilters/LinearTaskBuildBuckets.
  async listPullRequestBuckets(filters: LinearTaskBuildBucketFilters = {}): Promise<LinearTaskBuildBuckets> {
    const params: Record<string, string | number> = { ...sharedFilterParams(filters) }
    if (filters.ready_to_merge_page) params.ready_to_merge_page = filters.ready_to_merge_page
    if (filters.fixing_page) params.fixing_page = filters.fixing_page
    if (filters.in_progress_page) params.in_progress_page = filters.in_progress_page
    if (filters.failed_page) params.failed_page = filters.failed_page
    if (filters.per_page) params.per_page = filters.per_page

    const { data } = await api.get<{ buckets: LinearTaskBuildBuckets }>('/linear-tasks/pull-requests/buckets', { params })
    return data.buckets
  },

  // Merges every currently-passing repo PR on this task in one request — a
  // snapshot at request time, not the merge outcome itself, which happens
  // async via a queued job. Only callable from the Ready to Merge section's
  // Merge button.
  async mergePullRequests(id: string): Promise<MergePullRequestsResponse> {
    const { data } = await api.post<MergePullRequestsResponse>(`/linear-tasks/${id}/merge-pull-requests`)
    return data
  },

  // `instructions` is only sent when the retry flow's edit box was actually
  // changed — see RetryLinearTaskModal — so an unedited retry posts the same
  // empty body it always has and the API leaves the task's description alone.
  retry(id: string, instructions?: string): Promise<unknown> {
    return api.post(`/linear-tasks/${id}/retry`, instructions !== undefined ? { instructions } : undefined)
  },

  // Moves a Failed task to its other terminal state, Dismissed, for a
  // failure not worth retrying — takes it out of the Builds page's Failed
  // section for good, with no retry offered afterward.
  dismiss(id: string): Promise<unknown> {
    return api.post(`/linear-tasks/${id}/dismiss`)
  },

  show(id: string): Promise<LinearTask> {
    return unwrap(api.get<{ data: LinearTask }>(`/linear-tasks/${id}`))
  },

  // The persisted tail for a task's build — the live view seeds from this,
  // then appends anything a `progress.updated` broadcast delivers after.
  async getProgressEvents(id: string): Promise<LinearTaskProgressEvent[]> {
    return (await unwrap(api.get<{ data: LinearTaskProgressEvent[] }>(`/linear-tasks/${id}/progress-events`))) ?? []
  },

  // The structured, per-tool-call log for a task's build timeline page.
  async getEvents(id: string): Promise<LinearTaskEvent[]> {
    return (await unwrap(api.get<{ data: LinearTaskEvent[] }>(`/linear-tasks/${id}/events`))) ?? []
  },

  // Real test output, independent-verification verdicts, and QA
  // screenshots/gifs captured for this task's build — the same evidence
  // PublishBuildEvidenceAction posts as prose to Linear/GitHub, shaped here
  // for the SPA to render directly.
  getBuildEvidence(id: string): Promise<BuildEvidence> {
    return unwrap(api.get<{ data: BuildEvidence }>(`/linear-tasks/${id}/build-evidence`))
  },
}
